import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardMedia,
} from '@mui/material';
import { AutoFixHigh as AIIcon } from '@mui/icons-material';
import { getLocalImageUrl } from '../../../utils/imageUtils';

function ImageGenerationDialog({ open, onClose, onImageGenerated, accountId, microSiteId }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [size, setSize] = useState('1024x1024');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [revisedPrompt, setRevisedPrompt] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Generate and store the image in one API call
      const response = await fetch('/api/ai/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          prompt,
          size,
          accountId,
          microSiteId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to generate image');
      }

      const data = await response.json();
      
      // Convert the Wasabi URL to our local URL
      const localImageUrl = getLocalImageUrl(data.image.url, data.image.id);
      
      // Set the generated image from the response
      setGeneratedImage(localImageUrl);
      setRevisedPrompt(data.image.revisedPrompt || prompt);
      
      // Automatically use the image since it's already stored
      onImageGenerated(localImageUrl);
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Image with AI</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              multiline
              rows={3}
              fullWidth
              label="Image Description"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              required
            />

            <FormControl fullWidth>
              <InputLabel>Image Size</InputLabel>
              <Select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                label="Image Size"
              >
                <MenuItem value="1024x1024">Square (1024x1024)</MenuItem>
                <MenuItem value="1024x1792">Portrait (1024x1792)</MenuItem>
                <MenuItem value="1792x1024">Landscape (1792x1024)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !prompt.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <AIIcon />}
          >
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ImageGenerationDialog; 